// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import React, { FC, useEffect, useState, ReactElement } from "react";
import pLimit from "p-limit";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalTitle,
  Typography,
} from "@carrier-io/air-react";
import { apollo, Clients, useMutation } from "../../../apollo";
import { DEFAULT_REGION } from "../../../constants/regions";
import Button from "@carrier-io/air-react/Button";
import { CreateUserMutation } from "../../../graphql/mutations/createUser";
import { ParsedUsersType } from "../../../hooks/useBulkUsersCsvParser";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { CircularProgressWithLabel } from "../../circularProgress/CircularProgress";
import Tabs from "@carrier-io/air-react/Tabs";
import Tab from "@carrier-io/air-react/Tab";
import TabContext from "@carrier-io/air-react/TabContext";
import Box from "@carrier-io/air-react/Box";
import TabPanel from "@carrier-io/air-react/TabPanel";
import { useTheme } from "@carrier-io/air-react/styles";
import { exclude } from "../../../utils/exclude";
import { UploadContentError } from "./UploadContentError";
import { toCapitalLetter } from "../../../utils/toCapitalLetter";

type BulkAddUsersModalProps = {
  open: boolean;
  users: ParsedUsersType[];
  onClose: () => unknown;
  onContinue?: () => unknown;
};

const USER_REQUESTS_CONCURRENCY_LIMIT = 5;
const REQUEST_TIMEOUT = 10000;

const noop = () => undefined;

const createTimeoutPromise = (abort: () => void) =>
  new Promise<never>((_, reject) => {
    setTimeout(() => {
      abort();
      reject(new Error("Request timed out"));
    }, REQUEST_TIMEOUT);
  });

export const BulkAddUsersModal: FC<BulkAddUsersModalProps> = ({
  users,
  open,
  onClose,
  onContinue,
}) => {
  const theme = useTheme();
  const [runCreateUser] = useMutation(Clients.UPS, CreateUserMutation);
  const [isUploading, setIsUploading] = useState(false);
  const [successfullyUploaded, setSuccessfullyUploaded] = useState(0);
  const [tab, setTab] = useState("Errors");
  const [isError, setIsError] = useState(false);
  const [uploadingErrors, setUploadingErrors] = useState<
    { message: string; lineNumber: number }[]
  >([]);
  const [progress, setProgress] = useState(0);

  const handleSubmit = async () => {
    setIsUploading(true);
    const limit = pLimit(USER_REQUESTS_CONCURRENCY_LIMIT);
    const abortController = new AbortController();

    let completed = 0;
    let hasTimedOut = false;

    const abortRequests = () => {
      hasTimedOut = true;
      abortController.abort();
    };

    const handleSingleUserUpload = async (
      user: ParsedUsersType,
      signal: AbortSignal,
      abortRequests: () => void,
    ) => {
      if (hasTimedOut) {
        return;
      }

      const timeoutPromise = createTimeoutPromise(abortRequests);

      try {
        if (!navigator.onLine) {
          abortRequests();
          await Promise.reject(new Error("Network error"));
        }

        await Promise.race([
          runCreateUser({
            variables: {
              user: exclude(user, ["lineNumber"]),
              region: DEFAULT_REGION,
            },
            context: { fetchOptions: { signal } },
          }),
          timeoutPromise,
        ]);

        setSuccessfullyUploaded((prev: number) => prev + 1);
      } catch (error) {
        const knownError = error as apollo.ApolloError;

        if (
          knownError.message === "Request timed out" ||
          knownError.message === "Network error"
        ) {
          setIsError(true);
          setIsUploading(false);
        }

        const errorMessage =
          (knownError.graphQLErrors?.[0]?.extensions?.details as string) ||
          toCapitalLetter(knownError.message) ||
          "Unknown error";

        setUploadingErrors((prevErrors) => [
          ...prevErrors,
          {
            lineNumber: user.lineNumber,
            message: errorMessage,
          },
        ]);
      } finally {
        completed++;
        setProgress(Math.floor((completed / users.length) * 100));
      }
    };

    await Promise.all(
      users.map((user) =>
        limit(() =>
          handleSingleUserUpload(user, abortController.signal, abortRequests),
        ),
      ),
    );

    setUploadingErrors((prevErrors) =>
      prevErrors.sort((a, b) => a.lineNumber - b.lineNumber),
    );
    setIsUploading(false);
  };

  useEffect(() => {
    if (open && !isUploading) {
      setProgress(0);
      setSuccessfullyUploaded(0);
      setUploadingErrors([]);
      setIsUploading(false);
      setIsError(false);
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const error = (
    <Box display="flex" flexDirection="column" alignItems="center">
      <ErrorOutlineOutlinedIcon sx={{ color: theme.palette.error.main }} />
      <Typography variant="body1" color="error.main">
        {uploadingErrors.length} users failed to upload
      </Typography>
      <Box width="100%" maxHeight="144px" overflow="auto" marginTop="16px">
        {uploadingErrors.map((error, idx) => (
          <UploadContentError key={error.lineNumber + idx} error={error} />
        ))}
      </Box>
    </Box>
  );

  const success = (
    <Box display="flex" flexDirection="column" alignItems="center">
      <CheckCircleOutlineIcon sx={{ color: theme.palette.success.main }} />
      <Typography variant="body1" color="success.main">
        {successfullyUploaded} users successfully uploaded
      </Typography>
    </Box>
  );

  const tabs = {} as { Errors: ReactElement; Successes: ReactElement };

  if (isError) {
    tabs["Errors"] = error;
  } else if (!isUploading) {
    if (successfullyUploaded > 0) {
      tabs["Successes"] = success;
    }

    if (uploadingErrors.length > 0) {
      tabs["Errors"] = error;
    }
  }

  useEffect(() => {
    if (!isUploading) {
      if (successfullyUploaded > 0 && uploadingErrors.length === 0) {
        setTab("Successes");
      } else if (uploadingErrors.length > 0) {
        setTab("Errors");
      }
    }
  }, [isUploading, successfullyUploaded, uploadingErrors.length]);

  return (
    <Modal
      open={open}
      onClose={isUploading ? noop : onClose}
      maxWidth="xl"
      sx={{
        display: "flex",
        p: 1,
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
      }}
    >
      <ModalTitle clearable onClose={isUploading ? noop : onClose}>
        Bulk Upload Processing
      </ModalTitle>
      <ModalContent sx={{ minWidth: "628px" }}>
        <Box display="flex" flexDirection="column" alignItems="center">
          {isUploading ? (
            <CircularProgressWithLabel size={56} value={progress} />
          ) : (
            <Box width="100%">
              <TabContext value={tab}>
                <Tabs
                  indicatorVariant="line"
                  indicatorWidth="content"
                  value={tab}
                >
                  {Object.keys(tabs).map((key) => (
                    <Tab
                      key={key}
                      value={key}
                      onClick={() => setTab(key)}
                      label={
                        <Box display="flex" alignItems="center">
                          <Typography variant="body1">{key}</Typography>
                        </Box>
                      }
                    />
                  ))}
                </Tabs>
                {Object.entries(tabs).map(([key, value]) => (
                  <TabPanel key={key} value={key}>
                    {value}
                  </TabPanel>
                ))}
              </TabContext>
            </Box>
          )}
        </Box>
      </ModalContent>
      <ModalActions sx={{ justifyContent: "flex-end", padding: "0 24px 24px" }}>
        <Button variant="text" onClick={onClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button variant="outlined" onClick={onContinue} disabled={isUploading}>
          Continue to User List
        </Button>
      </ModalActions>
    </Modal>
  );
};
